import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, Clock, AlertTriangle, RotateCcw } from "lucide-react";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { MigrationTypeLabels } from "../../models/Statistics/MigrationType";
import { MigrationQueueStatus, QueueRefreshState } from "../../models/Recalculation";

/**
 * The lock says whether the order is being rewritten right now; the refresh state says whether one was asked for and
 * has not finished. A refresh waits for a free worker before it takes the lock, so without the second the panel
 * would read Idle for a refresh that has not started, or that gave up.
 */
const RefreshBadge: React.FC<{ status: MigrationQueueStatus }> = ({ status }) => {
    switch (status.refreshState) {
        case QueueRefreshState.Waiting:
            return (
                <Badge variant="secondary" className="gap-1.5">
                    <Clock className="size-3" />
                    Waiting to start
                </Badge>
            );
        case QueueRefreshState.Running:
            return (
                <Badge variant="warning" className="gap-1.5">
                    <Loader2 className="size-3 animate-spin" />
                    Starting
                </Badge>
            );
        case QueueRefreshState.Retrying:
            return (
                <Badge variant="warning" className="gap-1.5">
                    <RotateCcw className="size-3" />
                    Retrying after an error
                </Badge>
            );
        case QueueRefreshState.Failed:
            return (
                <Badge variant="destructive" className="gap-1.5">
                    <AlertTriangle className="size-3" />
                    Last refresh failed
                </Badge>
            );
        default:
            return (
                <Badge variant="success" className="gap-1.5">
                    <CheckCircle2 className="size-3" />
                    Idle
                </Badge>
            );
    }
};

interface MigrationQueueStatusPanelProps {
    organizationId?: string;
}

const formatHeartbeat = (lockedAt: string | null): string => {
    if (!lockedAt) return "";
    const seconds = Math.max(
        0,
        Math.floor((Date.now() - new Date(lockedAt).getTime()) / 1000)
    );
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s ago`;
};

const MigrationQueueStatusPanel: React.FC<MigrationQueueStatusPanelProps> = ({
    organizationId,
}) => {
    const organizationsRepository = new OrganizationsRepository();

    const statusQuery = useQuery({
        queryKey: [QUERY_KEYS.MIGRATION_QUEUE_STATUS, organizationId],
        queryFn: () =>
            organizationsRepository.getMigrationQueueStatus(organizationId!),
        enabled: !!organizationId,
        // Poll quickly while a recalculation is running; keep a slower idle poll so a
        // newly-started lock (picked up asynchronously by a background worker) is still caught.
        refetchInterval: (query) =>
            query.state.data?.some(
                (s) =>
                    s.isRunning ||
                    s.refreshState === QueueRefreshState.Waiting ||
                    s.refreshState === QueueRefreshState.Running ||
                    s.refreshState === QueueRefreshState.Retrying
            )
                ? 5000
                : 15000,
    });

    const statuses = statusQuery.data ?? [];

    return (
        <Card className="flex flex-col overflow-hidden min-h-0 p-4">
            <div className="flex justify-between items-start mb-3">
                <div>
                    <h3 className="text-sm font-semibold">
                        Migration Queue Status
                    </h3>
                    <p className="text-xs text-muted-foreground">
                        Per migration type: a refresh that is waiting, running
                        or failed. Updates automatically.
                    </p>
                </div>
                {statusQuery.isFetching && (
                    <p className="text-xs text-muted-foreground">Refreshing...</p>
                )}
            </div>

            <Separator className="mb-3" />

            <div className="overflow-y-auto flex-1">
                {statusQuery.isLoading ? (
                    <div className="space-y-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-[52px] rounded-md" />
                        ))}
                    </div>
                ) : statusQuery.isError ? (
                    <p className="text-sm text-destructive text-center mt-8">
                        Failed to load migration queue status.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {statuses.map((s) => (
                            <div
                                key={s.migrationType}
                                className="flex items-center justify-between rounded-md border p-3"
                            >
                                <span className="text-sm font-medium">
                                    {MigrationTypeLabels[s.migrationType] ??
                                        s.migrationType}
                                </span>
                                {s.isRunning ? (
                                    <Badge variant="warning" className="gap-1.5">
                                        <Loader2 className="size-3 animate-spin" />
                                        Running
                                        {s.lockedAt
                                            ? ` · ${formatHeartbeat(s.lockedAt)}`
                                            : ""}
                                    </Badge>
                                ) : (
                                    <RefreshBadge status={s} />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default MigrationQueueStatusPanel;
